/**
 * Shopify Variant Visualizer Component
 * 4-column layout: Formato | Prezzo/u (s.IVA) | s.IVA | c.IVA
 * + Reseller pricing with strikethrough
 * + Shopify translations
 */

const { useState, useEffect } = React;

const VariantVisualizer = ({ productData }) => {
  const [selectedVariant, setSelectedVariant] = useState(
    productData?.variants?.[0]?.id || null
  );
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const reseller = window.variantVisualizerReseller || { isReseller: false, discount: 0 };

  // Converte "€ 1.234,56" in 1234.56 (punto = migliaia, virgola = decimali)
  const parseEuro = (str) => parseFloat(String(str).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.'));

  const applyResellerDiscount = (priceStr) => {
    if (!reseller.isReseller || !reseller.discount || !priceStr || priceStr === '-') return priceStr;
    const numeric = parseEuro(priceStr);
    if (isNaN(numeric)) return priceStr;
    const discounted = numeric * (1 - reseller.discount);
    return '€ ' + discounted.toFixed(2).replace('.', ',');
  };

  const calcExcVAT = (incVATStr) => {
    if (!incVATStr || incVATStr === '-') return '-';
    const numeric = parseEuro(incVATStr);
    if (isNaN(numeric)) return '-';
    return '€ ' + (numeric / 1.22).toFixed(2).replace('.', ',');
  };

  const calcUnitExcVAT = (unitStr) => {
    if (!unitStr || unitStr === '-') return '-';
    const match = unitStr.match(/€\s*([\d.,]+)(\/.*)/);
    if (!match) return '-';
    const numeric = parseEuro(match[1]);
    if (isNaN(numeric)) return '-';
    return '€ ' + (numeric / 1.22).toFixed(2).replace('.', ',') + match[2];
  };

  useEffect(() => {
    const beYoursQuantityInput = document.querySelector('[name="quantity"]');
    if (beYoursQuantityInput) {
      beYoursQuantityInput.value = quantity;
      beYoursQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [quantity]);

  useEffect(() => {
    const fixProgressBarWidth = (bar) => {
      const inventoryQty = parseInt(bar.getAttribute('data-quantity') || bar.getAttribute('data-inventory-quantity') || 0);
      const max = parseInt(bar.getAttribute('data-max') || bar.getAttribute('max') || 100);
      const correctPercentage = Math.min((inventoryQty / max) * 100, 100);
      const currentWidth = bar.style.getPropertyValue('--progress-bar-width');
      if (currentWidth) {
        const currentValue = parseFloat(currentWidth);
        if (currentValue > 100 || currentValue !== correctPercentage) {
          bar.style.setProperty('--progress-bar-width', correctPercentage + '%');
        }
      }
    };
    const fixAllProgressBars = () => {
      document.querySelectorAll('progress-bar, [data-inventory-quantity]').forEach(fixProgressBarWidth);
    };
    fixAllProgressBars();
    const t1 = setTimeout(fixAllProgressBars, 50);
    const t2 = setTimeout(fixAllProgressBars, 200);
    const t3 = setTimeout(fixAllProgressBars, 500);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const bar = mutation.target;
          if (bar.tagName.toLowerCase() === 'progress-bar' || bar.hasAttribute('data-inventory-quantity')) {
            fixProgressBarWidth(bar);
          }
        }
      });
    });
    document.querySelectorAll('progress-bar, [data-inventory-quantity]').forEach(bar => {
      observer.observe(bar, { attributes: true, attributeFilter: ['style'] });
    });
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); observer.disconnect(); };
  }, []);

  const getCurrentPriceTier = (variant, qty) => {
    if (!variant.bulkPricing || variant.bulkPricing.length === 0) {
      return {
        unitPriceDisplay: variant.unitPriceDisplay || variant.per100g,
        excVAT: variant.excVAT,
        incVAT: variant.incVAT
      };
    }
    for (let i = variant.bulkPricing.length - 1; i >= 0; i--) {
      const tier = variant.bulkPricing[i];
      if (qty >= tier.min && qty <= tier.max) {
        return {
          unitPriceDisplay: tier.unitPriceDisplay || tier.per100g,
          excVAT: tier.excVAT,
          incVAT: tier.incVAT,
          range: tier.range,
          discount: tier.discount
        };
      }
    }
    return {
      unitPriceDisplay: variant.unitPriceDisplay || variant.per100g,
      excVAT: variant.excVAT,
      incVAT: variant.incVAT
    };
  };

  useEffect(() => {
    if (!selectedVariant) return;
    const currentVariant = productData.variants.find(v => v.id === selectedVariant);
    if (!currentVariant) return;

    document.querySelectorAll('[name="id"]').forEach(input => {
      if (input.value != selectedVariant) {
        input.value = selectedVariant;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    if (currentVariant.inventory_quantity !== undefined) {
      document.querySelectorAll('progress-bar, [data-inventory-quantity]').forEach(bar => {
        const inventoryQty = currentVariant.inventory_quantity || 0;
        bar.setAttribute('data-quantity', inventoryQty);
        bar.setAttribute('data-inventory-quantity', inventoryQty);
        const max = parseInt(bar.getAttribute('data-max') || bar.getAttribute('max') || 100);
        bar.style.setProperty('--progress-bar-width', Math.min((inventoryQty / max) * 100, 100) + '%');
      });
      document.querySelectorAll('.product__inventory-item strong, .product__inventory strong').forEach(strong => {
        strong.textContent = currentVariant.inventory_quantity;
      });
    }
  }, [selectedVariant, quantity, productData]);

  const handleVariantSelect = (variantId) => { setSelectedVariant(variantId); };

  const handleAddToCart = async (e) => {
    if (e) e.preventDefault();
    if (!selectedVariant || isAddingToCart) return;
    const currentVariant = productData.variants.find(v => v.id === selectedVariant);
    if (!currentVariant || !currentVariant.available) {
      setMessage({ text: window.variantVisualizerTranslations?.unavailable || 'Variante non disponibile', type: 'error' });
      return;
    }
    const productForm = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) { variantInput.value = selectedVariant; variantInput.disabled = false; }
      const quantityInput = productForm.querySelector('input[name="quantity"]');
      if (quantityInput) { quantityInput.value = quantity; }

      const currentTier = getCurrentPriceTier(currentVariant, quantity);
      if (currentVariant.bulkPricing && currentVariant.bulkPricing.length > 0) {
        let discountPercent;
        if (typeof currentTier.discount === 'number') {
          discountPercent = Math.round(currentTier.discount * 100);
        } else {
          const basePrice = parseEuro(currentVariant.incVAT);
          const tierPrice = parseEuro(currentTier.incVAT);
          discountPercent = Math.round(((basePrice - tierPrice) / basePrice) * 100);
        }
        if (discountPercent > 0) {
          productForm.querySelectorAll('input[name^="properties[_bulk"]').forEach(el => el.remove());
          const props = {
            'properties[_bulk_pricing_enabled]': 'true',
            'properties[_original_price]': currentVariant.incVAT,
            'properties[_discounted_price]': currentTier.incVAT,
            'properties[_discount_percent]': discountPercent + '%',
            'properties[_tier]': currentTier.range || 'Standard',
            'properties[_quantity]': quantity.toString()
          };
          Object.entries(props).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden'; input.name = name; input.value = value;
            productForm.appendChild(input);
          });
        }
      }
      const submitButton = productForm.querySelector('button[type="submit"][name="add"]');
      if (submitButton && !submitButton.disabled) { submitButton.click(); }
      else if (productForm.requestSubmit) { productForm.requestSubmit(); }
      else { productForm.submit(); }
      return;
    }
  };

  if (!productData || !productData.variants || productData.variants.length === 0) { return null; }

  // Colonne: formato | prezzo unitario | IVA escl. | IVA incl.
  // Colonne prezzi a larghezza fissa (cifre allineate, mai a capo); le misure cambiano per breakpoint tramite variabili CSS
  const gridCols = 'minmax(0, 1fr) var(--vv-col-unit) var(--vv-col-price) var(--vv-col-price)';
  const t = window.variantVisualizerTranslations || {};

  // "€ 9,36" -> "9,36 €"
  const euroAfter = (str) => {
    if (!str || str === '-') return '-';
    return String(str).replace(/€\s*([\d.,]+)/, '$1 €');
  };

  // Suffisso dell'unità ("/100g", "/kg", "/pz") di un prezzo unitario
  const unitSuffix = (str) => {
    const m = str && String(str).match(/\/\S+$/);
    return m ? m[0] : '';
  };

  const resellerActive = reseller.isReseller && reseller.discount;

  // Prezzo unitario IVA esclusa, con eventuale sconto rivenditore
  const unitCell = (unitInc) => {
    const unitExc = calcUnitExcVAT(unitInc);
    if (!resellerActive || unitExc === '-') return { original: unitExc, final: unitExc };
    return { original: unitExc, final: applyResellerDiscount(unitExc) + unitSuffix(unitExc) };
  };

  const priceCells = (incVAT) => {
    const excVAT = calcExcVAT(incVAT);
    return {
      exc: excVAT,
      inc: incVAT,
      finalExc: resellerActive ? applyResellerDiscount(excVAT) : excVAT,
      finalInc: resellerActive ? applyResellerDiscount(incVAT) : incVAT
    };
  };

  const rows = productData.variants.map(variant => {
    const isSelected = selectedVariant === variant.id;
    const tier = isSelected ? getCurrentPriceTier(variant, quantity) : null;
    const unitInc = isSelected
      ? (tier.unitPriceDisplay || '-')
      : (variant.unitPriceDisplay || variant.per100g || '-');
    const incVAT = isSelected ? (tier.incVAT || '-') : (variant.incVAT || '-');

    const showBulk = variant.bulkPricing && (variant.showBulkPricingAlways || isSelected);
    const tiers = showBulk ? variant.bulkPricing.map(bulk => ({
      range: bulk.range,
      active: isSelected && quantity >= bulk.min && quantity <= bulk.max,
      unit: unitCell(bulk.unitPriceDisplay || bulk.per100g || '-'),
      prices: priceCells(bulk.incVAT || '-')
    })) : [];

    return { variant, isSelected, unit: unitCell(unitInc), prices: priceCells(incVAT), tiers };
  });

  // Se tutte le righe usano la stessa unità, la mostro solo nell'intestazione: "(/100g)"
  const suffixes = new Set();
  rows.forEach(r => {
    [r.unit, ...r.tiers.map(tr => tr.unit)].forEach(u => { if (u.final !== '-') suffixes.add(unitSuffix(u.final)); });
  });
  const commonSuffix = suffixes.size === 1 ? [...suffixes][0] : null;
  const unitHeader = commonSuffix ? '(' + commonSuffix + ')' : (t.pricePerUnit || 'Prezzo/u');
  const stripSuffix = (str) => (commonSuffix && str !== '-') ? str.replace(commonSuffix, '') : str;

  const priceEl = (className, original, final) => React.createElement('div', { className },
    resellerActive && original !== '-'
      ? [React.createElement('span', { key: 'o', className: 'vv-price-original' }, euroAfter(original)), ' ', euroAfter(final)]
      : euroAfter(original)
  );

  const rowCells = (label, unit, prices, extraClass, withRadio, isSelected) =>
    React.createElement('div', { className: 'vv-row ' + (extraClass || '') },
      React.createElement('div', { className: 'vv-name' },
        withRadio && React.createElement('span', { className: 'vv-radio' + (isSelected ? ' checked' : '') },
          isSelected && React.createElement('svg', { viewBox: '0 0 12 12', width: 12, height: 12 },
            React.createElement('path', { d: 'M2.5 6.2l2.3 2.3 4.7-4.9', fill: 'none', stroke: '#fff', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' })
          )
        ),
        React.createElement('span', { className: 'vv-label' }, label)
      ),
      priceEl('vv-unit', stripSuffix(unit.original), stripSuffix(unit.final)),
      priceEl('vv-exc', prices.exc, prices.finalExc),
      priceEl('vv-inc', prices.inc, prices.finalInc)
    );

  return React.createElement('div', { className: 'vv' },
    React.createElement('style', {
      dangerouslySetInnerHTML: {
        __html: `
          .price:not(.variant-visualizer-price),
          product-form .product-form__quantity,
          product-form quantity-input,
          product-form button[type="submit"][name="add"],
          product-form .shopify-payment-button {
            display: none !important;
          }

          .vv {
            --vv-border: #E4DFD8;
            --vv-text: #1A1A1A;
            --vv-muted: #8A8580;
            --vv-accent: #E08A2E;
            --vv-item-hover: #FBF7F1;
            --vv-row-hover: rgba(224, 138, 46, 0.09);
            /* Misure: desktop qui; caratteri ridotti a <=1024px, spaziature a <=768px, telefono a <=480px */
            --vv-pad-x: 16px;
            --vv-gap: 12px;
            --vv-col-unit: 72px;
            --vv-col-price: 92px;
            --vv-fs-head: 12px;
            --vv-fs-name: 15px;
            --vv-fs-unit: 11px;
            --vv-fs-price: 15px;
            --vv-fs-inc: 16px;
            --vv-radio: 18px;
            --vv-tier-indent: 30px;
            color: var(--vv-text);
            font-family: var(--font-body-family, inherit);
            width: 100%;
            margin: 0 0 20px 0;
          }

          .vv-row {
            display: grid;
            grid-template-columns: ${gridCols};
            column-gap: var(--vv-gap);
            align-items: center;
          }

          .vv-header {
            padding: 0 calc(var(--vv-pad-x) + 1px) 8px;
            font-size: var(--vv-fs-head);
            color: var(--vv-muted);
          }

          .vv-header > div { text-align: right; }
          .vv-header > div:first-child { text-align: left; }
          .vv-header .vv-unit { font-size: var(--vv-fs-unit); }

          .vv-list {
            border: 1px solid var(--vv-border);
            border-radius: 10px;
            overflow: hidden;
          }

          .vv-item {
            padding: 8px var(--vv-pad-x);
            cursor: pointer;
            transition: background 0.2s ease, box-shadow 0.2s ease;
          }

          .vv-item + .vv-item { border-top: 1px solid var(--vv-border); }
          .vv-item:hover:not(.unavailable) { background: var(--vv-item-hover); }
          .vv-item.selected { background: #fff; box-shadow: inset 3px 0 0 var(--vv-accent); }
          .vv-item.unavailable { opacity: 0.5; cursor: not-allowed; }

          /* Ogni riga (variante e fasce di quantità) si evidenzia a tutta larghezza al passaggio del mouse */
          .vv-item > .vv-row {
            margin: 0 calc(var(--vv-pad-x) * -1);
            padding: 4px var(--vv-pad-x);
            transition: background 0.15s ease, color 0.15s ease;
          }
          .vv-item:not(.unavailable) > .vv-row:hover { background: var(--vv-row-hover); }
          .vv-item:not(.unavailable) > .vv-row:hover .vv-inc { color: var(--vv-accent); }
          .vv-tier .vv-name { padding-left: var(--vv-tier-indent); }
          .vv-tier.active-tier .vv-label { font-weight: 600; }

          .vv-name {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: var(--vv-fs-name);
            min-width: 0;
          }

          .vv-label { overflow-wrap: normal; word-break: normal; }
          .vv-tier .vv-label { white-space: nowrap; }

          .vv-radio {
            width: var(--vv-radio);
            height: var(--vv-radio);
            flex-shrink: 0;
            border: 1.5px solid var(--vv-text);
            border-radius: 50%;
            background: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .vv-radio.checked { background: var(--vv-text); }

          .vv-unit {
            font-size: var(--vv-fs-unit);
            text-align: right;
            white-space: nowrap;
          }

          .vv-exc,
          .vv-inc {
            font-size: var(--vv-fs-price);
            text-align: right;
            white-space: nowrap;
          }

          /* Prezzi con lo stile di .price / .price-item--regular del tema (component-price.css) */
          .vv-row:not(.vv-header) .vv-unit,
          .vv-row:not(.vv-header) .vv-exc,
          .vv-row:not(.vv-header) .vv-inc {
            font-family: var(--font-price-family, inherit);
            font-weight: 400;
            letter-spacing: 0.1rem;
            line-height: 1;
            color: var(--color-price, var(--vv-text));
          }
          .vv-row:not(.vv-header) .vv-inc { font-size: var(--vv-fs-inc); }

          .vv-price-original {
            position: relative;
            display: inline-block;
            font-size: 0.75em;
            font-weight: 300;
            color: rgba(var(--color-foreground, 26, 26, 26), 0.7);
            margin-right: 4px;
          }
          .vv-price-original::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            width: 100%;
            border-bottom: 0.1rem solid var(--color-sale-price, var(--vv-accent));
            opacity: 0.7;
            transform: rotate(-15deg);
          }

          .vv-reseller-badge {
            display: inline-block;
            background: var(--vv-text);
            color: #fff;
            font-size: 11px;
            font-weight: 700;
            padding: 3px 8px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 12px;
          }

          .vv-qty-label {
            margin: 18px 0 6px;
            font-size: 12px;
            color: var(--vv-muted);
          }

          .vv-controls {
            display: flex;
            gap: 16px;
            align-items: stretch;
          }

          .vv-qty {
            display: flex;
            flex: 0 1 240px;
            border: 1px solid var(--vv-border);
            background: #fff;
          }

          .vv-qty-btn {
            width: 44px;
            flex-shrink: 0;
            border: none;
            background: #fff;
            font-size: 18px;
            color: var(--vv-text);
            cursor: pointer;
          }

          .vv-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }

          .vv-qty-input {
            flex: 1;
            min-width: 40px;
            width: 100%;
            border: none;
            border-left: 1px solid var(--vv-border);
            border-right: 1px solid var(--vv-border);
            text-align: center;
            font-size: 15px;
            color: var(--vv-text);
            background: #fff;
            outline: none;
          }

          .vv-add-btn.variant-visualizer-add-btn {
            flex: 1;
            min-height: 46px;
            background: var(--vv-accent);
            color: #fff;
            border: none;
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-transform: uppercase;
            cursor: pointer;
          }

          .vv-add-btn:hover:not(:disabled) { filter: brightness(0.95); }
          .vv-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }

          .vv-message {
            margin-top: 12px;
            padding: 10px 16px;
            font-size: 14px;
            text-align: center;
          }

          .vv-message.success { background: #EAF4F0; color: #2E7D4F; }
          .vv-message.error { background: #FEF0F0; color: #C0392B; }

          /* Caratteri: tablet e sotto */
          @media (max-width: 1024px) {
            .vv { --vv-fs-name: 14px; --vv-fs-price: 14px; --vv-fs-inc: 15px; }
          }

          /* Spaziature: mobile. Quantità e pulsante si impilano a tutta larghezza */
          @media (max-width: 768px) {
            .vv { --vv-pad-x: 12px; --vv-gap: 8px; --vv-col-unit: 66px; --vv-col-price: 76px; --vv-fs-head: 11px; --vv-tier-indent: 26px; }
            .vv-item { padding: 6px var(--vv-pad-x); }
            .vv-name { gap: 8px; }
            .vv-row:not(.vv-header) .vv-unit,
            .vv-row:not(.vv-header) .vv-exc,
            .vv-row:not(.vv-header) .vv-inc { letter-spacing: 0.05rem; }
            .vv-controls { flex-direction: column; gap: 10px; }
            .vv-qty { flex: 0 0 auto; width: 100%; }
            .vv-qty-btn { width: 48px; }
            .vv-qty-input { min-height: 44px; font-size: 15px; }
            .vv-add-btn.variant-visualizer-add-btn { width: 100%; min-height: 48px; font-size: 13px; }
          }

          /* Telefono: si rimpicciolisce il formato, i prezzi restano leggibili */
          @media (max-width: 480px) {
            .vv { --vv-pad-x: 10px; --vv-gap: 8px; --vv-col-unit: 62px; --vv-col-price: 62px; --vv-fs-head: 10px; --vv-fs-name: 12px; --vv-fs-unit: 10px; --vv-fs-price: 13px; --vv-fs-inc: 14px; --vv-radio: 16px; --vv-tier-indent: 24px; }
          }
        `
      }
    }),

    // Inventory
    React.createElement('div', {
      ref: (el) => {
        if (el && !el.dataset.inventoryMoved) {
          const beYoursInventory = document.querySelector('.product__inventory, ul[class*="inventory"]');
          if (beYoursInventory) { el.appendChild(beYoursInventory); el.dataset.inventoryMoved = 'true'; }
        }
      },
      style: { marginBottom: '16px' }
    }),

    // Reseller badge
    resellerActive && React.createElement('div', { className: 'vv-reseller-badge' },
      reseller.label || 'Prezzo Rivenditore', ' -' + Math.round(reseller.discount * 100) + '%'
    ),

    // Header
    React.createElement('div', { className: 'vv-row vv-header' },
      React.createElement('div', null, t.packSize || 'Formato'),
      React.createElement('div', { className: 'vv-unit' }, unitHeader),
      React.createElement('div', null, t.vatExcl || 'IVA Escl.'),
      React.createElement('div', null, t.vatIncl || 'IVA Incl.')
    ),

    // Variants
    React.createElement('div', { className: 'vv-list' },
      rows.map(r => React.createElement('div', {
        key: r.variant.id,
        className: `vv-item ${r.isSelected ? 'selected' : ''} ${!r.variant.available ? 'unavailable' : ''}`,
        onClick: () => r.variant.available && handleVariantSelect(r.variant.id)
      },
        rowCells(r.variant.title || r.variant.option1 || 'Variante', r.unit, r.prices, '', true, r.isSelected),
        r.tiers.map((tr, idx) => React.createElement(React.Fragment, { key: idx },
          rowCells(tr.range, tr.unit, tr.prices, 'vv-tier' + (tr.active ? ' active-tier' : ''), false, false)
        ))
      ))
    ),

    // Controls
    React.createElement('div', { className: 'vv-qty-label' }, t.quantity || 'Quantità'),
    React.createElement('div', { className: 'vv-controls' },
      React.createElement('div', { className: 'vv-qty' },
        React.createElement('button', {
          type: 'button',
          className: 'vv-qty-btn',
          onClick: () => setQuantity(Math.max(1, quantity - 1)),
          disabled: quantity <= 1
        }, '−'),
        React.createElement('input', {
          type: 'text',
          inputMode: 'numeric',
          pattern: '[0-9]*',
          className: 'vv-qty-input',
          value: quantity,
          onChange: (e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            setQuantity(Math.max(1, parseInt(value) || 1));
          },
          onBlur: (e) => {
            if (!e.target.value || parseInt(e.target.value) < 1) setQuantity(1);
          }
        }),
        React.createElement('button', {
          type: 'button',
          className: 'vv-qty-btn',
          onClick: () => setQuantity(quantity + 1)
        }, '+')
      ),
      React.createElement('button', {
        type: 'button',
        className: 'vv-add-btn variant-visualizer-add-btn',
        onClick: handleAddToCart,
        disabled: isAddingToCart || !selectedVariant || !productData.variants.find(v => v.id === selectedVariant)?.available
      }, isAddingToCart
        ? (t.adding || 'Aggiungendo...')
        : (t.addToCart || 'Aggiungi al Carrello'))
    ),

    // Message
    message.text && React.createElement('div', {
      className: `vv-message ${message.type}`
    }, message.text)
  );
};

window.VariantVisualizer = VariantVisualizer;
